import { birthdayDisplayToIso, birthdayIsoToDisplay } from '@frontend/duck-tape';
import type { MakeInputProps } from '@frontend/forms';
import { useEffect, useState } from '@frontend/react';
import { Text, YStack } from '@frontend/web-react';
import { safeCss } from '@frontend/web-utils';
import type { InputBaseProps as MInputBaseProps } from '@mantine/core';
import { InputBase } from '@mantine/core';
import { IMaskInput } from 'react-imask';
import type { InputLabelProps } from '../../InputLabel';
import { InputLabel } from '../../InputLabel';

type BirthdayInputMaskProps = MakeInputProps<
  MaybeUndefined<DateISOString>,
  {
    error?: string;
    labelTextProps?: InputLabelProps['textProps'];
  }
>;

const classNames: MInputBaseProps['classNames'] = {
  input: safeCss('text-p-md h-[56px] rounded-sm focus-within:border-textPrimary'),
  root: safeCss('gap-y-sm flex flex-col flex-1'),
};

export const BirthdayInputMask = ({
  error,
  label = 'Birthday',
  labelTextProps,
  onBlur,
  onChange,
  value: birthdayFromForm,
}: BirthdayInputMaskProps) => {
  const [displayValue, setDisplayValue] = useState(() => birthdayIsoToDisplay(birthdayFromForm));
  const [monthPart = '', dayPart = '', yearPart = ''] = displayValue.split('/');
  const yearDigits = yearPart.replace(/\D/g, '');
  const hasIncompleteYear =
    monthPart.length === 2 && dayPart.length === 2 && yearDigits.length > 0 && yearDigits.length < 4;
  const shownError = hasIncompleteYear ? 'Birth year must be 4 digits' : error;

  useEffect(() => {
    const newDisplayValue = birthdayIsoToDisplay(birthdayFromForm);
    const currentIso = birthdayDisplayToIso(displayValue);
    if (birthdayFromForm !== currentIso) {
      setDisplayValue(newDisplayValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- displayValue intentionally excluded to prevent infinite sync loops during typing
  }, [birthdayFromForm]);

  return (
    <YStack gapY="sm">
      <InputBase
        classNames={classNames}
        component={IMaskInput}
        inputMode="numeric"
        label={label ? <InputLabel label={label} textProps={labelTextProps} /> : null}
        mask="00/00/0000"
        // Using onAccept instead of onChange because react-imask reliably triggers onAccept for both typing and pasting
        onAccept={(value: string) => {
          setDisplayValue(value);
          const isoDate = birthdayDisplayToIso(value);
          if (isoDate) {
            onChange(isoDate as DateISOString);
          } else {
            onChange('' as DateISOString);
          }
        }}
        onBlur={onBlur}
        placeholder="MM/DD/YYYY"
        value={displayValue}
      />
      {shownError ? (
        <Text color="danger" type="caption">
          {shownError}
        </Text>
      ) : null}
    </YStack>
  );
};
